
import React from 'react';
import { AnalysisResult, COLORS } from '../types';
import { createOpportunityChartData } from '../utils';
import { ResponsiveContainer, RadialBarChart, RadialBar, Legend, Tooltip, Cell } from 'recharts';
import CustomTooltip from '../CustomTooltip';

type OpportunitiesTabProps = {
  analysisResult: AnalysisResult;
};

const OpportunitiesTab = ({
  analysisResult
}: OpportunitiesTabProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10 space-y-4">
        <h4 className="text-lg font-semibold text-primary mb-4">Revenue Opportunities</h4>
        <div className="space-y-3">
          {analysisResult.revenueOpportunities?.map((opp, index) => (
            <div 
              key={index} 
              className="bg-secondary/50 rounded-lg p-4 border border-primary/10"
            >
              <h5 className="font-medium mb-2 text-white">{opp.opportunity}</h5>
              <div className="flex justify-center items-center mb-2">
                <span className="text-xs text-white/70">Potential:</span>
                <span 
                  className={`px-2 py-1 rounded text-xs ${
                    opp.potential.toLowerCase().includes('high') 
                      ? 'bg-green-900/30 text-green-400' 
                      : opp.potential.toLowerCase().includes('medium')
                      ? 'bg-yellow-900/30 text-yellow-400'
                      : 'bg-red-900/30 text-red-400'
                  }`}
                >
                  {opp.potential}
                </span>
              </div>
              <div className="flex justify-center items-center">
                <span className="text-xs text-white/70">Timeline:</span>
                <span 
                  className={`px-2 py-1 rounded text-xs ${
                    opp.timeline.toLowerCase().includes('short') 
                      ? 'bg-green-900/30 text-green-400' 
                      : opp.timeline.toLowerCase().includes('medium')
                      ? 'bg-yellow-900/30 text-yellow-400'
                      : 'bg-blue-900/30 text-blue-400'
                  }`}
                >
                  {opp.timeline}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10">
        <h4 className="text-lg font-semibold text-primary mb-4">Opportunity Potential Matrix</h4>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart 
              cx="50%" 
              cy="45%" 
              innerRadius="20%" 
              outerRadius="85%" 
              data={createOpportunityChartData(analysisResult)} 
              startAngle={180} 
              endAngle={0}
            >
              <RadialBar 
                label={{
                  position: 'insideStart',
                  fill: '#fff'
                }} 
                background 
                dataKey="value"
              >
                {createOpportunityChartData(analysisResult).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </RadialBar>
              <Legend 
                iconSize={10} 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="left" 
                wrapperStyle={{
                  fontSize: '12px',
                  color: '#fff',
                  paddingTop: '20px',
                  paddingLeft: '10px',
                  textAlign: 'left'
                }} 
              />
              <Tooltip 
                content={<CustomTooltip />} 
                formatter={(value) => [`Potential: ${value}%`, 'Revenue Impact']} 
                labelFormatter={(label) => [`Opportunity: ${label}`]} 
              />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-400">
            Data Points Explanation: Each arc represents revenue potential percentage (0-100%). Higher values (e.g., 90%) indicate high-priority opportunities with significant revenue impact, while lower values (e.g., 60%) represent moderate opportunities requiring strategic consideration.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OpportunitiesTab;
